import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiShoppingCart, FiArrowLeft, FiStar } from 'react-icons/fi';
import { getProduct } from '../services/api';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selSize, setSelSize]   = useState('');
  const [qty, setQty]           = useState(1);
  const [imgIdx, setImgIdx]     = useState(0);

  useEffect(() => {
    getProduct(id)
      .then(res => {
        setProduct(res.data);
        if (res.data.sizes?.length > 0) setSelSize(res.data.sizes[0].size);
      })
      .catch(() => navigate('/shop'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleAddToCart = () => {
    if (!selSize) return toast.error('Please select a size');
    addToCart(product, selSize, qty);
    toast.success('Added to cart!');
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
      <div className="skeleton rounded-2xl aspect-square" />
      <div className="space-y-4">
        <div className="skeleton h-6 rounded w-3/4" />
        <div className="skeleton h-8 rounded w-1/2" />
        <div className="skeleton h-20 rounded" />
      </div>
    </div>
  );

  if (!product) return null;

  const displayPrice = product.salePrice || product.price;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-gray-400 hover:text-yamass-black mb-8 transition-colors"
      >
        <FiArrowLeft size={16} /> Back
      </button>

      <div className="grid md:grid-cols-2 gap-12">
        {/* Images */}
        <div>
          <div className="rounded-2xl overflow-hidden bg-yamass-cream aspect-square mb-4">
            {product.images?.[imgIdx] ? (
              <img src={product.images[imgIdx]} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300 text-4xl">👕</div>
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-colors ${
                    imgIdx === i ? 'border-yamass-accent' : 'border-transparent'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <p className="text-yamass-accent text-sm uppercase tracking-widest mb-2">{product.category}</p>
          <h1 className="font-display text-3xl font-bold text-yamass-black mb-3">{product.name}</h1>

          {/* Price */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl font-bold text-yamass-black">৳{displayPrice.toLocaleString()}</span>
            {product.salePrice && (
              <span className="text-lg text-gray-400 line-through">৳{product.price.toLocaleString()}</span>
            )}
          </div>

          {/* Rating */}
          {product.ratings?.average > 0 && (
            <div className="flex items-center gap-2 mb-4">
              <div className="flex">
                {[1,2,3,4,5].map(s => (
                  <FiStar key={s} size={14} className={s <= Math.round(product.ratings.average) ? 'text-yamass-accent fill-yamass-accent' : 'text-gray-200 fill-gray-200'} />
                ))}
              </div>
              <span className="text-sm text-gray-400">({product.ratings.count} reviews)</span>
            </div>
          )}

          <p className="text-gray-600 leading-relaxed mb-6">{product.description}</p>

          {/* Fabric */}
          {product.fabric && (
            <p className="text-sm text-gray-400 mb-6">Fabric: <span className="text-yamass-black font-medium">{product.fabric}</span></p>
          )}

          {/* Size selector */}
          {product.sizes?.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-semibold text-yamass-black mb-3">
                Size: <span className="text-yamass-accent">{selSize}</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map(s => (
                  <button
                    key={s.size}
                    onClick={() => setSelSize(s.size)}
                    disabled={s.stock === 0}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                      selSize === s.size
                        ? 'bg-yamass-black text-white border-yamass-black'
                        : s.stock === 0
                        ? 'border-gray-100 text-gray-300 cursor-not-allowed'
                        : 'border-gray-200 text-gray-700 hover:border-yamass-accent'
                    }`}
                  >
                    {s.size}
                    {s.stock === 0 && <span className="ml-1 text-xs">(out)</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity + Add to cart */}
          <div className="flex items-center gap-4">
            <div className="flex items-center border border-gray-200 rounded-full overflow-hidden">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="px-4 py-3 text-lg hover:bg-gray-50 transition-colors">−</button>
              <span className="px-4 py-3 text-sm font-medium min-w-[3rem] text-center">{qty}</span>
              <button onClick={() => setQty(q => q + 1)} className="px-4 py-3 text-lg hover:bg-gray-50 transition-colors">+</button>
            </div>
            <button
              onClick={handleAddToCart}
              className="flex-1 flex items-center justify-center gap-2 bg-yamass-black text-white py-4 rounded-full font-semibold hover:bg-yamass-accent transition-colors text-sm"
            >
              <FiShoppingCart size={18} /> Add to Cart
            </button>
          </div>

          {/* Tags */}
          {product.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {product.tags.map(t => (
                <span key={t} className="bg-yamass-cream text-gray-500 text-xs px-3 py-1 rounded-full">{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
